import { useMemo } from 'react';

import { useFormatter } from '@eneco/i18n';

import {
  buildElectricitySection,
  buildGasSection,
  buildGeothermalHeatingSection,
  buildTapWaterSection,
  buildWarmthExchangeSetSection,
  buildWarmthSection,
  hasWarmthExchangeSetSection,
  type ElectricitySectionViewModel,
  type GasSectionViewModel,
  type GeothermalHeatingSectionViewModel,
  type ProductSectionsBuilderContext,
  type TapWaterSectionViewModel,
  type WarmthExchangeSetSectionViewModel,
  type WarmthSectionViewModel,
} from './builders/product-sections';
import { getRedeliveryDetailsForOffer, type RedeliveryBuilderContext } from './builders/redelivery';
import type { CostsType } from './builders/types';
import { getRedeliveryTariffPopoverDescription, getTotalUsageFromTaxBrackets } from './price-details-overview.helpers';
import type { PriceDetailsOverviewTextualData } from './price-details-overview.types';
import { FlowConsumerContextEnergy } from '../../../context';
import {
  checkOfferIncludesDynamicPricingProduct,
  checkOfferIncludesNonDynamicPricingProduct,
  energyTypeIncludes,
} from '../../../shared/helpers';
import { checkIfOfferHasTimeOfUseProduct } from '../../../shared/helpers/misc';
import { EnergyType, type CostDetails, type Offer } from '../../../shared/types';
import { OfferType } from '../../../shared/types/enums';

export type { PriceDetailsOverviewTextualData };

export interface UsePriceDetailsOverviewParams {
  flowContext: FlowConsumerContextEnergy;
  offer?: Offer;
  textualData?: PriceDetailsOverviewTextualData;
}

export interface PriceDetailsOverviewViewModel {
  flowContext: FlowConsumerContextEnergy;
  offer?: Offer;
  textualData?: PriceDetailsOverviewTextualData;
  energyType: EnergyType;
  costDetails?: CostDetails;
  usages?: Exclude<Offer['usages'], undefined>;
  electricitySection: ElectricitySectionViewModel | null;
  gasSection: GasSectionViewModel | null;
  warmthSection: WarmthSectionViewModel | null;
  tapWaterSection: TapWaterSectionViewModel | null;
  warmthExchangeSetSection: WarmthExchangeSetSectionViewModel | null;
  geothermalHeatingSection: GeothermalHeatingSectionViewModel | null;
  costsType: CostsType;
  unit: ReturnType<typeof useFormatter>['unit'];
  currency: ReturnType<typeof useFormatter>['currency'];
  shouldShowDynamicContractNotification: boolean;
  shouldShowHybridContractNotification: boolean;
  shouldShowVariablePricingNotification: boolean;
}

export const usePriceDetailsOverview = ({
  flowContext,
  offer,
  textualData,
}: UsePriceDetailsOverviewParams): PriceDetailsOverviewViewModel => {
  const {
    energyType = EnergyType.Electricity,
    hasDoubleMeter,
    isSME,
    solarPanelsOutput,
    hasSolarPanels,
  } = flowContext;
  const { costDetails, discountType, usages, estimatedUsages } = offer || {};

  const hasTimeOfUse = checkIfOfferHasTimeOfUseProduct(offer);

  const { electricityNormal = 0, electricityLow = 0, gas = 0 } = usages || {};
  const {
    low: estimatedElectricityLow,
    high: estimatedElectricityHigh,
    usage = 0,
    futureUsage = 0,
    nettedUsage = 0,
  } = estimatedUsages ?? {};
  const electricityRedeliveryRemainder = costDetails?.electricityTotal?.electricityRedeliveryRefund?.netUsage ?? 0;

  const hasElectricityTaxBrackets =
    costDetails?.electricityEnergyTaxDetails && Object.keys(costDetails.electricityEnergyTaxDetails).length > 0;
  const electricityUsageTotal = hasElectricityTaxBrackets
    ? getTotalUsageFromTaxBrackets(costDetails.electricityEnergyTaxDetails)
    : (electricityNormal ?? 0) + (electricityLow ?? 0);

  const hasGasTaxBrackets = costDetails?.gasEnergyTaxDetails && Object.keys(costDetails.gasEnergyTaxDetails).length > 0;
  const gasUsageTotal = hasGasTaxBrackets ? getTotalUsageFromTaxBrackets(costDetails.gasEnergyTaxDetails) : (gas ?? 0);

  const { currency, number, unit } = useFormatter();

  const costsType: CostsType = isSME ? 'vatExcluded' : 'vatIncluded';

  const isDynamicOffer = !discountType?.includes(OfferType.Variable) && checkOfferIncludesDynamicPricingProduct(offer);
  const isDynamicProduct = checkOfferIncludesDynamicPricingProduct(offer);

  const shouldShowDynamicContractNotification =
    isDynamicOffer &&
    !checkOfferIncludesNonDynamicPricingProduct(offer) &&
    !!textualData?.costDetails?.dynamicPricingNotification?.value?.variant;

  const shouldShowHybridContractNotification =
    isDynamicOffer &&
    checkOfferIncludesNonDynamicPricingProduct(offer) &&
    !!textualData?.costDetails?.hybridNotification?.value?.content;

  const shouldShowVariablePricingNotification = Boolean(
    discountType?.includes(OfferType.Variable) &&
      textualData?.costDetails?.variableProductNotification?.value?.content,
  );

  const shouldShowDynamicPricingLabels =
    isDynamicProduct && costDetails?.electricityTotal?.electricityRedeliveryRefund?.netUsage !== 0;

  const shouldShowTaxBracketsPriceComponents = !!(
    costDetails?.electricityEnergyTaxDetails ?? costDetails?.gasEnergyTaxDetails
  );

  const hasCPQResponse = Boolean(
    costDetails?.electricityFutureCostDetails?.electricityRedeliveryRefund?.usage != null &&
      costDetails?.electricityFutureCostDetails?.redeliveryCost?.usage != null,
  );

  const hasRedeliveryCost =
    !!costDetails?.electricityTotal?.redeliveryCost?.[costsType] &&
    costDetails.electricityTotal.redeliveryCost[costsType] !== 0;

  const hasRedeliveryCostTotal =
    !!costDetails?.electricityTotal?.redeliveryCostTotal?.[costsType] &&
    costDetails.electricityTotal.redeliveryCostTotal[costsType] !== 0;

  const shouldShowRedeliveryCost = hasRedeliveryCost || hasRedeliveryCostTotal;

  const redeliveryBuilderContext = useMemo<RedeliveryBuilderContext>(
    () => ({
      offer,
      costDetails,
      costsType,
      discountType,
      futureRate: offer?.productFutureRate,
      shouldShowDynamicPricingLabels,
      electricityCostDetails: textualData?.electricityCostDetails,
      formatter: { currency, unit },
    }),
    [
      offer,
      costDetails,
      costsType,
      discountType,
      shouldShowDynamicPricingLabels,
      textualData?.electricityCostDetails,
      currency,
      unit,
    ],
  );

  const redeliveryPopoverContent = useMemo(
    () =>
      getRedeliveryTariffPopoverDescription({
        costDetails,
        solarPanelsOutput,
        popoverDescription: isDynamicOffer
          ? textualData?.electricityCostDetails?.redeliveryDynamicTariffPopoverDescription?.value
          : textualData?.electricityCostDetails?.redeliveryTariffPopoverDescription?.value,
        usage: usage ?? undefined,
        nettedUsage: nettedUsage ?? undefined,
        futureUsage: futureUsage ?? undefined,
        formatElectricity: unit.electricity,
      }),
    [
      costDetails,
      solarPanelsOutput,
      isDynamicOffer,
      textualData?.electricityCostDetails?.redeliveryDynamicTariffPopoverDescription?.value,
      textualData?.electricityCostDetails?.redeliveryTariffPopoverDescription?.value,
      usage,
      nettedUsage,
      futureUsage,
      unit.electricity,
    ],
  );

  const productSectionsContext = useMemo<ProductSectionsBuilderContext>(
    () => ({
      costDetails,
      costsType,
      textualData,
      hasDoubleMeter,
      hasTimeOfUse,
      shouldShowDynamicPricingLabels,
      shouldShowTaxBracketsPriceComponents,
      electricityNormal: electricityNormal ?? 0,
      electricityLow: electricityLow ?? 0,
      gas: gas ?? 0,
      estimatedElectricityLow,
      estimatedElectricityHigh,
      electricityUsageTotal,
      gasUsageTotal,
      isDynamicProduct,
      formatter: { currency, number, unit },
    }),
    [
      costDetails,
      costsType,
      textualData,
      hasDoubleMeter,
      hasTimeOfUse,
      shouldShowDynamicPricingLabels,
      shouldShowTaxBracketsPriceComponents,
      electricityNormal,
      electricityLow,
      gas,
      estimatedElectricityLow,
      estimatedElectricityHigh,
      electricityUsageTotal,
      gasUsageTotal,
      isDynamicProduct,
      currency,
      number,
      unit,
    ],
  );

  const electricitySection = useMemo(() => {
    if (!energyTypeIncludes(energyType, EnergyType.Electricity)) {
      return null;
    }

    const redelivery =
      hasSolarPanels && shouldShowRedeliveryCost
        ? {
            summary: {
              text: textualData?.electricityCostDetails?.redeliveryLabel?.value,
              value: costDetails?.electricityTotal?.redeliveryCostTotal?.[costsType]
                ? costDetails?.electricityTotal?.redeliveryCostTotal?.[costsType]
                : costDetails?.electricityTotal?.redeliveryCost?.[costsType],
              popoverContent: redeliveryPopoverContent,
            },
            details: getRedeliveryDetailsForOffer(
              redeliveryBuilderContext,
              hasCPQResponse,
              electricityRedeliveryRemainder,
              solarPanelsOutput ?? 0,
            ),
          }
        : undefined;

    return buildElectricitySection({ context: productSectionsContext, redelivery });
  }, [
    energyType,
    hasSolarPanels,
    shouldShowRedeliveryCost,
    textualData?.electricityCostDetails?.redeliveryLabel?.value,
    costDetails,
    costsType,
    redeliveryPopoverContent,
    redeliveryBuilderContext,
    hasCPQResponse,
    electricityRedeliveryRemainder,
    solarPanelsOutput,
    productSectionsContext,
  ]);

  const gasSection = useMemo(
    () => (energyTypeIncludes(energyType, EnergyType.Gas) ? buildGasSection(productSectionsContext) : null),
    [energyType, productSectionsContext],
  );

  const warmthSection = useMemo(
    () => (energyTypeIncludes(energyType, EnergyType.Warmth) ? buildWarmthSection(productSectionsContext) : null),
    [energyType, productSectionsContext],
  );

  const tapWaterSection = useMemo(
    () => (energyTypeIncludes(energyType, EnergyType.TapWater) ? buildTapWaterSection(productSectionsContext) : null),
    [energyType, productSectionsContext],
  );

  const warmthExchangeSetSection = useMemo(
    () =>
      hasWarmthExchangeSetSection(productSectionsContext)
        ? buildWarmthExchangeSetSection(productSectionsContext)
        : null,
    [productSectionsContext],
  );

  const geothermalHeatingSection = useMemo(
    () =>
      energyTypeIncludes(energyType, EnergyType.GeothermalHeating)
        ? buildGeothermalHeatingSection(productSectionsContext)
        : null,
    [energyType, productSectionsContext],
  );

  return {
    flowContext,
    offer,
    textualData,
    energyType,
    costDetails,
    usages,
    electricitySection,
    gasSection,
    warmthSection,
    tapWaterSection,
    warmthExchangeSetSection,
    geothermalHeatingSection,
    costsType,
    unit,
    currency,
    shouldShowDynamicContractNotification,
    shouldShowHybridContractNotification,
    shouldShowVariablePricingNotification,
  };
};
